import React, { useState } from 'react';
import RecompenseStatusBadge from './RecompenseStatusBadge';
import { toRecompenseCardView } from '../../utils/recompensePresentation';

/**
 * F2.2b / FIX-2 — Liste cartes + boutons d’action (design system Admin existant).
 */
export default function RecompenseList({
  recompenses,
  busyId = null,
  onEdit,
  onActivate,
  onSuspend,
  onArchive,
  onMoveUp,
  onMoveDown,
}) {
  const rows = Array.isArray(recompenses) ? recompenses : [];
  const movable = rows.filter((r) => r && r.statut !== 'ARCHIVEE');
  const [menuId, setMenuId] = useState(null);

  const btnBase =
    'inline-flex items-center justify-center px-3.5 py-1.5 rounded-lg text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <ul className="space-y-3" data-testid="recompense-list">
      {rows.map((row, index) => {
        const card = toRecompenseCardView(row, index);
        const archived = row.statut === 'ARCHIVEE';
        const busy = busyId && (busyId === row.id || busyId === '*');
        const movableIndex = movable.findIndex((r) => r.id === row.id);
        const canUp = !archived && movableIndex > 0;
        const canDown = !archived && movableIndex >= 0 && movableIndex < movable.length - 1;
        const menuOpen = menuId === row.id;

        return (
          <li
            key={card.key}
            className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm"
            data-testid="recompense-list-item"
          >
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-lg font-bold text-gray-900 tracking-tight">
                    {card.effectTitle}
                  </h3>
                  <RecompenseStatusBadge code={card.statut.code} label={card.statut.label} />
                </div>
                <p className="text-base text-gray-800">{card.coutLabel}</p>
                <p className="text-sm text-gray-500">{card.typeLabel}</p>
                {card.periode && (
                  <p className="text-xs text-gray-500">{card.periode}</p>
                )}
                {!archived && (
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    <button
                      type="button"
                      data-testid={`recompense-edit-${row.id}`}
                      disabled={busy}
                      onClick={() => onEdit?.(row)}
                      className={`${btnBase} border border-[#4A148C] text-[#4A148C] hover:bg-purple-50`}
                    >
                      Modifier
                    </button>
                    {(row.statut === 'BROUILLON' || row.statut === 'SUSPENDUE') && (
                      <button
                        type="button"
                        data-testid={`recompense-activate-${row.id}`}
                        disabled={busy}
                        onClick={() => onActivate?.(row)}
                        className={`${btnBase} bg-green-600 text-white hover:bg-green-700`}
                      >
                        Activer
                      </button>
                    )}
                    {row.statut === 'ACTIVE' && (
                      <button
                        type="button"
                        data-testid={`recompense-suspend-${row.id}`}
                        disabled={busy}
                        onClick={() => onSuspend?.(row)}
                        className={`${btnBase} border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100`}
                      >
                        Suspendre
                      </button>
                    )}
                    <div className="relative">
                      <button
                        type="button"
                        aria-label="Plus d’actions"
                        data-testid={`recompense-more-${row.id}`}
                        disabled={busy}
                        onClick={() => setMenuId(menuOpen ? null : row.id)}
                        className={`${btnBase} border border-gray-200 text-gray-600 hover:bg-gray-50`}
                      >
                        ⋯
                      </button>
                      {menuOpen && (
                        <div className="absolute left-0 z-10 mt-1 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                          <button
                            type="button"
                            data-testid={`recompense-archive-${row.id}`}
                            className="block w-full px-3 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50"
                            onClick={() => {
                              setMenuId(null);
                              onArchive?.(row);
                            }}
                          >
                            Archiver
                          </button>
                          <button
                            type="button"
                            aria-label="Monter"
                            data-testid={`recompense-up-${row.id}`}
                            disabled={!canUp}
                            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            onClick={() => {
                              setMenuId(null);
                              onMoveUp?.(row);
                            }}
                          >
                            Monter ↑
                          </button>
                          <button
                            type="button"
                            aria-label="Descendre"
                            data-testid={`recompense-down-${row.id}`}
                            disabled={!canDown}
                            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                            onClick={() => {
                              setMenuId(null);
                              onMoveDown?.(row);
                            }}
                          >
                            Descendre ↓
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div
                className="hidden sm:flex w-20 h-20 rounded-xl bg-purple-50 border border-purple-100 items-center justify-center text-xl font-bold text-[#4A148C] shrink-0"
                aria-hidden
              >
                {card.hero}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
